import { APICONFIG } from '@config/api.config';
import { APIService } from '@services/api/common-services';
import { useQuery } from '@tanstack/react-query';
import { IQueryResponse } from '@services/api';
import { ISiteSummaryList } from '@components/siteSummary';
import { ErrorHandler } from '@helper/errorHandler';
import { useCheckRxdb } from '@hooks/checkRxdb';
import { setRxdb } from '@helper/utils';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ProfileData } from '@services/hooks/profile';

export const useSiteSummaryListing = (forceFetch = false): IQueryResponse<Array<ISiteSummaryList>> => {
	const { isDbChecked, shouldFetch } = useCheckRxdb('site_summary_list');
	const { result: profile } = useGetRxDb<ProfileData>('profile');

	// Check if profile is loaded and user is not RBDM
	const isProfileLoaded = profile && profile.length > 0;
	const userRole = profile[0]?.usertype_c;
	const checkSSMFilterEnable = userRole === 'BDE';
	const shouldEnableQuery = isProfileLoaded && isDbChecked && shouldFetch;

	const fetchSiteSummaryListing = async (): Promise<Array<ISiteSummaryList>> => {
		try {
			const response = await APIService.getData<Array<ISiteSummaryList>>(APICONFIG.SITE_SUMMARY_LISTING, {
				limit: '0',
			});
			if (response) {
				let filteredResponse = response;
				if (checkSSMFilterEnable) {
					filteredResponse = response.filter((site) => {
						return site?.site_current_status_c !== 'Upcoming';
					});
				}
				await setRxdb('site_summary_list', filteredResponse);
				return filteredResponse;
			}
			return response;
		} catch (error) {
			ErrorHandler(error);
			return [];
		}
	};

	const { data, error, isLoading, isPending, isFetching, refetch } = useQuery({
		queryKey: ['siteSummaryListing'],
		queryFn: fetchSiteSummaryListing,
		enabled: shouldEnableQuery || forceFetch,
		refetchOnWindowFocus: false,
		retry: false,
	});

	return {
		data,
		error,
		isLoading,
		isPending,
		isFetching,
		refetch,
	};
};
